import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Row, Col ,InputGroup} from 'react-bootstrap';
import axios from 'axios';
import { FaEye, FaEyeSlash } from "react-icons/fa";

function Signup({ onLogin }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    pin: '',
    voiceCode: '',
    realNumber: '',
    language: 'en'
  });
  const [step, setStep] = useState(1); // 1 = Signup Form, 2 = Verify OTP
  const [smsConsent, setSmsConsent] = useState(false); // Unchecked by default
  const [deliveryChannel, setDeliveryChannel] = useState("call"); // "sms" or "call"
  const [otpCode, setOtpCode] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Active countdown timer when rate-limited
  React.useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setError("");
          return 0;
        }
        const next = prev - 1;
        setError(`Please wait ${next}s before requesting another verification code.`);
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  React.useEffect(() => {
    let interval = null;
    if (step === 2 && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [step, resendTimer]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError("");
    setInfoMessage("");

    if (formData.pin.length !== 4 || !/^\d{4}$/.test(formData.pin)) {
      setError("PIN must be exactly 4 digits");
      return;
    }

    if (formData.voiceCode.length < 3) {
      setError("Voice code must be at least 3 characters");
      return;
    }

    // Phone number validation
    const phoneRegex = /^\+\d{7,15}$/;
    if (!phoneRegex.test(formData.realNumber)) {
      setError(
        "Please enter a valid phone number with country code (e.g., +1234567890)"
      );
      return;
    }

    setLoading(true);

    try {
      const res = await axios.post("/api/auth/send-signup-otp", {
        realNumber: formData.realNumber,
        email: formData.email,
        smsConsent: smsConsent,
        disclosureVersion: "v2.0",
      });

      const returnedChannel = res.data?.channel || (smsConsent ? "sms" : "call");
      setDeliveryChannel(returnedChannel);
      setStep(2);
      setResendTimer(60);
      setCanResend(false);
      setInfoMessage(
        returnedChannel === "sms"
          ? "Your verification code will be sent by text message."
          : "Your verification code will be delivered by an automated phone call."
      );
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to send verification code";
      const retryAfter = err.response?.data?.retryAfter;
      let seconds = retryAfter;
      if (!seconds && typeof msg === "string") {
        const match = msg.match(/Please wait (\d+)s/i);
        if (match) {
          seconds = parseInt(match[1], 10);
        }
      }

      if (seconds && seconds > 0) {
        setCountdown(seconds);
        setError(`Please wait ${seconds}s before requesting another verification code.`);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend || countdown > 0) return;
    setError("");
    setInfoMessage("");
    setLoading(true);
    try {
      const res = await axios.post("/api/auth/send-signup-otp", {
        realNumber: formData.realNumber,
        email: formData.email,
        smsConsent: smsConsent,
        disclosureVersion: "v2.0",
      });
      const returnedChannel = res.data?.channel || deliveryChannel;
      setDeliveryChannel(returnedChannel);
      setResendTimer(60);
      setCanResend(false);
      setInfoMessage(
        returnedChannel === "sms"
          ? "New verification code sent by text message."
          : "An automated phone call will deliver your new verification code."
      );
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to resend verification code";
      const retryAfter = err.response?.data?.retryAfter;
      let seconds = retryAfter;
      if (!seconds && typeof msg === "string") {
        const match = msg.match(/Please wait (\d+)s/i);
        if (match) {
          seconds = parseInt(match[1], 10);
        }
      }

      if (seconds && seconds > 0) {
        setCountdown(seconds);
        setError(`Please wait ${seconds}s before requesting another verification code.`);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setInfoMessage("");

    if (!otpCode || otpCode.trim().length === 0) {
      setError("Please enter the 6-digit verification code");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post("/api/auth/verify-signup-otp", {
        ...formData,
        code: otpCode.trim(),
        smsConsent: smsConsent,
      });
      onLogin(response.data.user, response.data.token);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.message || "Verification failed. Please check the code and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      className="d-flex align-items-center justify-content-center"
      style={{ minHeight: "100vh", paddingTop: "50px", paddingBottom: "50px" }}
    >
      <Card style={{ width: "100%", maxWidth: "600px" }}>
        <Card.Body className="p-5">
          <div className="text-center mb-4">
            <img
              src="/icon.png"
              alt="CallBunker Logo"
              className="mb-3"
              style={{ width: "122px", height: "85px" }}
            />
            {step === 1 ? (
              <>
                <h2>Create CallBunker Account</h2>
                <p className="text-muted">Set up your call protection</p>
              </>
            ) : (
              <>
                <h2>Verify Your Number</h2>
                <p className="text-muted">
                  {deliveryChannel === "sms" ? (
                    <>
                      Enter the verification code sent by text message to{" "}
                      <strong>{formData.realNumber}</strong>
                    </>
                  ) : (
                    <>
                      An automated phone call to{" "}
                      <strong>{formData.realNumber}</strong> will deliver your verification code.
                    </>
                  )}
                </p>
              </>
            )}
          </div>

          {error && <Alert variant="danger">{error}</Alert>}
          {infoMessage && <Alert variant="success">{infoMessage}</Alert>}

          {step === 1 ? (
            <Form onSubmit={handleSendOtp}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Full Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="you@example.com"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>

                <InputGroup className="position-relative">
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="Enter your password"
                    className="pe-5"
                  />

                  <InputGroup.Text
                    as="button"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="border-0 bg-transparent position-absolute end-0 top-50 translate-middle-y"
                    style={{ zIndex: 10, cursor: "pointer" }}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </InputGroup.Text>
                </InputGroup>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>4-Digit PIN</Form.Label>
                    <Form.Control
                      type="text"
                      name="pin"
                      value={formData.pin}
                      onChange={handleChange}
                      required
                      maxLength={4}
                      pattern="\d{4}"
                      placeholder="1234"
                    />
                    <Form.Text className="text-muted">
                      Callers will enter this to reach you
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Voice Code</Form.Label>
                    <Form.Control
                      type="text"
                      name="voiceCode"
                      value={formData.voiceCode}
                      onChange={handleChange}
                      required
                      placeholder="e.g., sunshine"
                    />
                    <Form.Text className="text-muted">
                      Callers can say this instead of PIN
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Your Personal Phone Number</Form.Label>
                    <Form.Control
                      type="tel"
                      name="realNumber"
                      value={formData.realNumber}
                      onChange={handleChange}
                      required
                      placeholder="+1234567890"
                    />
                    <Form.Text className="text-muted">
                      Must include country code (e.g., +1)
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Language</Form.Label>
                    <Form.Select
                      name="language"
                      value={formData.language}
                      onChange={handleChange}
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Voice prompt language
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              {/* Separate SMS Consent Checkbox (A2P 10DLC Compliant - Unchecked by Default) */}
              <Form.Group className="mb-3 text-start" controlId="smsConsentCheckbox">
                <Form.Check id="sms-consent-checkbox" className="d-flex align-items-start">
                  <Form.Check.Input
                    type="checkbox"
                    id="sms-consent-checkbox"
                    checked={smsConsent}
                    onChange={(e) => setSmsConsent(e.target.checked)}
                    style={{
                      border: "2px solid #212529",
                      backgroundColor: smsConsent ? "#1e3c72" : "#ffffff",
                      borderColor: smsConsent ? "#1e3c72" : "#212529",
                      minWidth: "18px",
                      minHeight: "18px",
                      marginTop: "2px",
                      cursor: "pointer",
                    }}
                  />
                  <Form.Check.Label
                    htmlFor="sms-consent-checkbox"
                    className="ms-2"
                    style={{
                      fontSize: "0.85rem",
                      color: "#0c0c0c",
                      lineHeight: "1.4",
                      cursor: "pointer",
                    }}
                  >
                    I agree to receive a one-time SMS verification code from CallBunker at the number provided. Standard message and data rates may apply.
                  </Form.Check.Label>
                </Form.Check>
              </Form.Group>

              {/* Terms of Use & Privacy Policy Links (Separate from SMS Checkbox) */}
              <div className="small text-center mb-3 text-muted">
                By creating an account, you agree to our{" "}
                <a
                  href="https://callbunker.com/callbunker-terms-of-use/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none text-info"
                >
                  Terms of Use
                </a>
                <span className="mx-2 text-muted">|</span>
                <a
                  href="https://callbunker.com/callbunker-privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none text-info"
                >
                  Privacy Policy
                </a>
              </div>

              <Button
                variant="primary"
                type="submit"
                className="w-100 mb-3"
                disabled={loading || countdown > 0}
              >
                {countdown > 0
                  ? `Please wait (${countdown}s)`
                  : loading
                  ? "Sending Verification Code..."
                  : "Continue to Verification"}
              </Button>

              <div className="text-center">
                <p className="mb-0">
                  Already have an account? <Link to="/login">Login here</Link>
                </p>
              </div>
            </Form>
          ) : (
            <Form onSubmit={handleVerifyOtp}>
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">6-Digit Verification Code</Form.Label>
                <Form.Control
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  placeholder="Enter 6-digit code (e.g. 123456)"
                  maxLength={6}
                  required
                  style={{
                    fontSize: "24px",
                    letterSpacing: "6px",
                    textAlign: "center",
                  }}
                />
              </Form.Group>

              <Button
                variant="success"
                type="submit"
                className="w-100 mb-3 py-2 fw-bold"
                disabled={loading}
              >
                {loading ? "Verifying Code..." : "Verify & Complete Signup"}
              </Button>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button
                  variant="link"
                  className="p-0 text-decoration-none"
                  onClick={handleResendOtp}
                  disabled={!canResend || loading}
                >
                  {canResend
                    ? deliveryChannel === "sms"
                      ? "Resend Code (SMS)"
                      : "Call Me Again (Phone Call)"
                    : deliveryChannel === "sms"
                    ? `Resend text in ${resendTimer}s`
                    : `Call again in ${resendTimer}s`}
                </Button>
                <Button
                  variant="link"
                  className="p-0 text-decoration-none text-secondary"
                  onClick={() => {
                    setStep(1);
                    setError("");
                    setInfoMessage("");
                  }}
                  disabled={loading}
                >
                  Change Phone Number
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export default Signup;
